// Core imports
import React, { Fragment, useEffect, useState } from 'react';
import { t } from 'i18next';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';

// Component Imports
import { Button, Checkbox, Col, Collapse, Row, Space, Spin, Tooltip, Typography, Upload } from 'antd';
import { WarningOutlined } from '@ant-design/icons';
import FormSectionHeader from '../UI/FormSectionHeader';
import { ArrowRightLongIcon } from '../Icons/ArrowRightLongIcon';
import TranslationResultModal from '../Modals/TranslationResultModal';
import TranslationExportModal from '../Modals/TranslationExportModal';

// Misc Imports
import { TRANSLATION_FILE_TYPES, TRANSLATION_REFERENCES_COLUMN } from '../../constants/translations';
import { REQUIRED_FIELD_MISSING_ERROR, generateFinalTranslationWarnings, generateLanguageWarningFlags, getUploadedFileParsedData, mergeTranslatedEntities } from '../../utils/misc/handleImportContentTranslation';
import { useBotLanguage } from '../../utils/hooks/useBotLanguage';
import { isEmptyObject } from '../../utils/is/isEmptyObject';
import { useLazyFetchActivitiesQuery } from '../../store/activities/activitiesApi';
import { useLazyFetchAutomatedAnswersQuery } from '../../store/automatedAnswers/automatedAnswersApi';
import { LANGUAGE_OPTIONS } from '../../constants/main';
import { useUpdateBotMutation } from '../../store/bots/botsApi';
import { getCurrentQueryState } from '../../store/utils/getCurrentQueryState';
import { onExportTranslations } from '../../utils/misc/handleExportContentTranslation';
import { NODE_TYPES_PROPERTIES } from '../../constants/flowGraph';
import { truncate } from '../../utils/string/truncate';
import { useUpdateChapterSourcesMutation } from '../../store/chapterSources/chapterSourcesApi';
import { isEmptyArray } from '../../utils/is/isEmptyArray';
import openConsoleAlertMessage from '../../utils/misc/openConsoleAlertMessage';

const { Dragger } = Upload;

const IMPORT_PROCESS_STEPS = {
  FILE_UPLOAD: 'file-upload',
  PROCESS_CONTENT: 'process-content'
};

const TRUNCATE_LIMIT = 25;

const _selectFromResult = (result) => {
  const dataMap = new Map();
  result?.items?.forEach(item => {
    dataMap.set(item.id, item);
  });

  return {
    ...result,
    itemsMap: dataMap
  };
};

const ImportTranslationsForm = ({
  onUpdateComplete = () => {},
  onClickNavigateToExport = () => {},
  onCancel = () => {}
}) => {
  const { orgId, tenantId } = useParams();
  const { primaryBotLanguage, botLanguages } = useBotLanguage();

  const [currentImportProcessStep, setCurrentImportProcessStep] = useState(IMPORT_PROCESS_STEPS.FILE_UPLOAD);
  const [fileList, setFileList] = useState([]);
  const [parsedData, setParsedData] = useState({});
  const [warningFlaggedData, setWarningFlaggedData] = useState({});
  const [languageData, setLanguageData] = useState([]);
  const [contentChangesData, setContentChangesData] = useState({});
  const [isProcessingFiles, setIsProcessingFiles] = useState(false);
  const [isTranslationResultsModalOpen, setIsTranslationResultsModalOpen] = useState(false);
  const [updatedEntitiesNames, setUpdatedEntitiesNames] = useState([]);
  const [entitiesNamesToExport, setEntitiesNamesToExport] = useState([]);
  const [isDisplayExportModalOpen, setIsDisplayExportModalOpen] = useState(false);
  const [updatedEntities, setUpdatedEntities] = useState({});

  const [fetchActivities] = useLazyFetchActivitiesQuery();
  const [fetchAutomatedAnswers] = useLazyFetchAutomatedAnswersQuery();
  const { data: bot } = getCurrentQueryState('fetchBot', { orgId, tenantId });
  const [updateBot, { isLoading: isUpdatingBot }] = useUpdateBotMutation({ fixedCacheKey: `updateTenantSettings` });
  const [updateChapterSources, { isLoading: isUpdatingChapterSources }] = useUpdateChapterSourcesMutation();

  /* ------- Misc Handlers ------- */

  const handleCheckboxChange = (index) => {
    const newLanguageItems = [...languageData];
    newLanguageItems[index].isEnabled = !newLanguageItems[index].isEnabled;
    setLanguageData(newLanguageItems);
  };

  const onAddLanguageClick = async (langCode, handleCheckboxChange) => {

    const valuesToUpdate = {
      settings: {
        ...bot.settings,
        languages: {
          ...bot.settings.languages,
          additional: [...bot.settings.languages.additional, langCode]
        }
      }
    };

    const isUpdateSuccessful = await updateBot({ orgId, tenantId, data: valuesToUpdate }).unwrap();

    if(isUpdateSuccessful) {
      handleCheckboxChange();
    }
  };

  const handleRemove = () => {
    setFileList([]);
  };

  /* ------- Entity fetch and update logic ------- */
  const updateEntityData = async (data) => {
    const extractedEntityKeys = Object.keys(data);
    const dataToUpdate = extractedEntityKeys.flatMap(key => data[key]);
    const isUpdateSuccessful = await updateChapterSources({ orgId, tenantId, data: dataToUpdate, entityKeys: extractedEntityKeys }).unwrap();
    return isUpdateSuccessful;
  };

  const fetchEntityData = async (data) => {
    const extractedEntityKeys = Array.from(new Set(data?.flatMap(item => Object.keys(item?.data) || '').filter(item => item?.length > 0)));
    const completeData = await Promise.all(
      extractedEntityKeys.map(async entityKey => {
        try {
          const fetchedEntityData = await fetchMatchingEntity(entityKey);
          return { [entityKey]: fetchedEntityData };
        } catch (err) {
          console.error(err);
          return { [entityKey]: null };
        }
      })
    );
    return Object.assign({}, ...completeData);
  };

  const fetchMatchingEntity = async (entityKey) => {
    let result;
    switch (entityKey) {
      case 'flows':
        result = await fetchActivities({ orgId, tenantId }, true).unwrap();
        return _selectFromResult(result);
      case 'automated-answers':
        result = await fetchAutomatedAnswers({ orgId, tenantId }, true).unwrap();
        return _selectFromResult(result);
      default:
        return Promise.reject(new Error('Invalid entity key'));
    }
  };

  /* ------- File Processing and translation generation logic ------- */

  const onClickUpload = async () => {
    setIsProcessingFiles(true);
    try {
      const data = await getUploadedFileParsedData({
        file: fileList[0],
        primaryLanguage: primaryBotLanguage,
        currentLanguages: botLanguages.filter(language => language !== primaryBotLanguage)
      });

      if(!isEmptyObject(data)) {
        setParsedData(data);
      } else {
        setIsProcessingFiles(false);
        setCurrentImportProcessStep(IMPORT_PROCESS_STEPS.PROCESS_CONTENT);
      }
    } catch(err) {
      setIsProcessingFiles(false);
      if(err.message === REQUIRED_FIELD_MISSING_ERROR) {
        const requiredFields = [primaryBotLanguage, TRANSLATION_REFERENCES_COLUMN];
        openConsoleAlertMessage({ type: 'error', message: t('One of the required fields ( {{requiredFields}} ) is missing.', { requiredFields: requiredFields.join(',') }) });
      } else {
        openConsoleAlertMessage({ type: 'error', message: t('There was an error reading your file.') });
      }
    }
  };

  useEffect(() => {
    if(!isEmptyObject(parsedData)) {
      const dataWithWarningFlags = generateLanguageWarningFlags({
        data: parsedData,
        primaryLanguage: primaryBotLanguage,
        enabledLanguages: botLanguages.filter(lang => lang !== primaryBotLanguage)
      });

      if(isEmptyObject(dataWithWarningFlags)) {
        setIsProcessingFiles(false);
        setCurrentImportProcessStep(IMPORT_PROCESS_STEPS.PROCESS_CONTENT);
      } else {
        setWarningFlaggedData(dataWithWarningFlags);
      }
    } else {
      setIsProcessingFiles(false);
    }
  }, [parsedData]);

  useEffect(() => {
    if (!isEmptyObject(warningFlaggedData)) {
      (async () => {
        try {
          const entitiesData = await fetchEntityData(warningFlaggedData);

          if (warningFlaggedData) {
            const finalData = generateFinalTranslationWarnings({
              translationsData: warningFlaggedData,
              entitiesData,
              primaryLanguage: primaryBotLanguage,
              enabledLanguages: botLanguages.filter((lang) => lang !== primaryBotLanguage)
            });

            setLanguageData(finalData?.languageData);
            setContentChangesData(finalData?.entityWarnings);
            setCurrentImportProcessStep(IMPORT_PROCESS_STEPS.PROCESS_CONTENT);
          }
        } catch (err) {
          setIsProcessingFiles(false);
          openConsoleAlertMessage({ type: 'error', message: t('There was an error reading your file.') });
        }
      })();
    }
  }, [warningFlaggedData]);

  useEffect(() => {
    if(languageData?.filter(item => item.isEnabled)?.length > 0) {
      (async () => {
        try {
          const dataToImport = languageData?.filter(item => item.isEnabled);
          const entitiesData = await fetchEntityData(dataToImport);
          const mergedData = mergeTranslatedEntities(dataToImport, entitiesData);

          const hasTranslatedEntities = Object.values(mergedData).some(arr => arr?.length > 0);

          if(hasTranslatedEntities) {
            setUpdatedEntities(mergedData);
          } else {
            setUpdatedEntities({});
          }

          setIsProcessingFiles(false);
        } catch(err) {
          setIsProcessingFiles(false);
          setUpdatedEntities({});
        }

      })();

    } else {
      setIsProcessingFiles(false);
    }
  }, [languageData]);

  const onImportClick = async () => {

    const isUpdateSuccessful = await updateEntityData(updatedEntities);

    if(isUpdateSuccessful) {
      setIsTranslationResultsModalOpen(true);
      const extractedEntityKeys = Object.keys(updatedEntities);
      const entitiesLabels = extractedEntityKeys.flatMap(key => updatedEntities[key].map(item => item?.name[primaryBotLanguage]));
      setUpdatedEntitiesNames(entitiesLabels);
    }
  };

  const onClickExportTranslations = async () => {
    let importedEntities = {};
    const entitiesData = await fetchEntityData(languageData);

    languageData.forEach(languageItem => {
      const { data } = languageItem;

      Object.entries(data).forEach(([key, value]) => {

        if(!(key in importedEntities)) {
          importedEntities[key] = [];
        }

        value.forEach(entityItem => {
          if(!importedEntities[key].find(item => item.id === entityItem.entityId)) {
            if(entitiesData[key]?.itemsMap?.has(entityItem.entityId)) {
              importedEntities[key].push(entitiesData[key]?.itemsMap?.get(entityItem.entityId));
            }
          }
        });
      });
    });

    const extractedEntityKeys = Object.keys(importedEntities);
    const entitiesLabels = extractedEntityKeys.flatMap(key => importedEntities[key].map(item => item?.name[primaryBotLanguage]));

    setEntitiesNamesToExport(entitiesLabels);
    const fileType = Object.values(TRANSLATION_FILE_TYPES)?.find(fileType => fileList[0].name.endsWith(fileType.extension))?.key === TRANSLATION_FILE_TYPES.CSV.key ? TRANSLATION_FILE_TYPES.CSV.key : TRANSLATION_FILE_TYPES.POT.key;
    onExportTranslations({
      fileType,
      options: {
        exportData: importedEntities,
        primaryExportLanguage: primaryBotLanguage,
        timezone: bot?.timezone,
        fileName: bot?.displayName,
        exportLanguages: botLanguages.filter(language => language !== primaryBotLanguage)
      }
    });
    setIsDisplayExportModalOpen(true);
  };

  /* --------- Rendering logic --------- */

  const renderStepHeader = (step) => {
    const headers = {
      [IMPORT_PROCESS_STEPS.FILE_UPLOAD]: (
        <Space direction="vertical" className="pb-4 w-100" size="middle">
          <Typography.Text className="fs-base fw-400">{t('Upload Translated Content:')}</Typography.Text>
        </Space>
      ),
      [IMPORT_PROCESS_STEPS.PROCESS_CONTENT]: (
        <Space direction="vertical" className="pb-4 w-100" size="middle">
          <Typography.Text className="fs-base fs-lg fw-400">
            {t('Uploaded file:')}
          </Typography.Text>
          <Typography.Text italic className="fs-base pt-4 fw-700">
            {`${t('Selected file name: ')} ${fileList[0]?.name || ''}`}
          </Typography.Text>
        </Space>
      )
    };
    return headers[step] || null;
  };

  const renderStepMainContent = (step) => {
    const mainContents = {
      [IMPORT_PROCESS_STEPS.FILE_UPLOAD]: (
        <Fragment>
          {renderUploadButton()}
        </Fragment>
      ),
      [IMPORT_PROCESS_STEPS.PROCESS_CONTENT]: (
        <Fragment>
          {renderTranslationReport()}
        </Fragment>
      )
    };
    return mainContents[step] || null;
  };

  const renderLanguageItem = (languageItem, index) => {
    const shouldDisableCheckbox = languageItem?.isPrimary || languageItem?.isInvalid || languageItem?.isDuplicate || !botLanguages.includes(languageItem.normalizedLanguageCode);
    const languageLabel = Object.values(LANGUAGE_OPTIONS).find(lang => lang.key === languageItem?.normalizedLanguageCode)?.label;

    return (
      <Fragment>
        <Row wrap={true} className="flex-row mb-3">
          <Col xs={5}>
            <Checkbox
              onChange={() => handleCheckboxChange(index)}
              disabled={shouldDisableCheckbox}
              checked={!shouldDisableCheckbox && languageItem?.isEnabled}
            >
              {languageLabel}
            </Checkbox>
          </Col>
          <Col xs={19}>
            <Typography.Text className="fw-500">
              {getWarningText(languageItem, languageLabel)}
              {
                !!bot && !languageItem.isPrimary && !languageItem.isDuplicate && !languageItem.isInvalid && !botLanguages.includes(languageItem.normalizedLanguageCode) && (
                  <Fragment>
                    <Typography.Text
                      onClick={() => onAddLanguageClick(
                        languageItem?.normalizedLanguageCode,
                        () => {
                          handleCheckboxChange(index);
                        }
                      )}className={classNames('ml-1 text-link text-dec-underline', {
                        'pointers-none opacity-50': isUpdatingBot
                      })}
                    >
                      {t('Add this language to your agent.')}
                      {
                        isUpdatingBot && (
                          <Spin size={'default'} spinning={isUpdatingBot}/>
                        )
                      }
                    </Typography.Text>
                  </Fragment>
                )
              }
            </Typography.Text>
          </Col>
        </Row>
      </Fragment>
    );
  };

  const getWarningText = (item, label) => {
    let warningText = null;
    if(item.isPrimary) {
      warningText = t(`${label} is an agent primary language so translation will be ignored.`);
    }

    if(item?.isDuplicate && !warningText) {
      warningText = t(`${label} is duplicate upload so translation will be ignored.`);
    }

    if(!botLanguages.includes(item.normalizedLanguageCode) && !warningText) {
      warningText = t(`${label} is not an agent language.`);
    }

    if(item?.isOverwritten && !warningText) {
      warningText = t(`There is already a ${label} translation. New translation will overwrite all currently existing content in this language in the same flow.`);
    }

    return <Fragment>
      {
        warningText
        && <Fragment>
          <WarningOutlined className="mr-1 text-warning"/> {warningText}
        </Fragment>
      }
    </Fragment>;
  };

  const renderContentChangesItem = (key, value) => {

    return (
      <Fragment>
        <Row className="mb-3">
          <Col xs={24}>
            <Space direction="horizontal" className="mb-2">
              <Typography.Text>
                { `Flow: ` }
              </Typography.Text>
              <Typography.Text className="fw-500">
                {`"${t(key)}"`}
              </Typography.Text>
              <Typography.Text>
                {t('changes in the following nodes:')}
              </Typography.Text>
            </Space>
          </Col>
          <Col xs={24}>
            <Space direction="vertical" className="pl-10" size="small">
              {
                value.map(item => (
                  <Space direction="horizontal">
                    <Typography.Text>
                      {NODE_TYPES_PROPERTIES[item?.type].label?.toLowerCase()}:
                    </Typography.Text>
                    <Typography>
                      {item?.status}:
                    </Typography>
                    <Tooltip
                      title={item?.originalContent?.length >= TRUNCATE_LIMIT ? item?.originalContent : null}
                    >
                      <Typography.Text italic className="text-dec-line-through">
                        {truncate(item?.originalContent, TRUNCATE_LIMIT, '...')}
                      </Typography.Text>
                    </Tooltip>
                    <Typography.Text>
                      <ArrowRightLongIcon />
                    </Typography.Text>
                    <Tooltip
                      title={item?.currentContent?.length >= TRUNCATE_LIMIT ? item?.currentContent: null}
                    >
                      <Typography.Text italic>
                        {truncate(item?.currentContent, TRUNCATE_LIMIT, '...')}
                      </Typography.Text>
                    </Tooltip>
                  </Space>
                ))
              }
            </Space>
          </Col>
        </Row>
      </Fragment>
    );
  };

  const renderTranslationReport = () => {
    return (
      <Fragment>
        {
          isEmptyObject(updatedEntities) ? (
            <Fragment>
              <Row>
                <Col xs={24}>
                  <Row className="mb-1">
                    <Col xs={24}>
                      <Typography.Text className="fw-500">
                        <WarningOutlined className="mt-1 text-warning"/> {t('The uploaded file does not contain any matching flows.')}
                      </Typography.Text>
                    </Col>
                  </Row>
                  <Row className="mb-4 ml-3">
                    <Col xs={24}>
                      <Typography.Text className="text-link ml-1 mr-1 cursor-pointer" onClick={() => onClickNavigateToExport()}>
                        {t('Click here')}
                      </Typography.Text>
                      <Typography.Text>
                        {t('to select flows for exporting.')}
                      </Typography.Text>
                    </Col>
                  </Row>
                </Col>
              </Row>
            </Fragment>
          ) : (
            <Fragment>
              <Row>
                <Col xs={24}>
                  <Collapse defaultActiveKey={['1']} ghost>
                    <Collapse.Panel
                      showArrow={false}
                      header={<FormSectionHeader title={t('Languages')} />}
                      key="1"
                    >
                      <Row className="mb-4">
                        <Col xs={24}>
                          <Typography.Text className="fw-500">
                            {
                              languageData?.length
                                ? t('The uploaded file contains flows in languages:')
                                : <Fragment><WarningOutlined className="mt-1 text-warning"/> {t('The uploaded file does not contain flows in any additional languages other than the primary language.')} </Fragment>
                            }
                          </Typography.Text>
                        </Col>
                      </Row>
                      <Row hidden={!languageData?.length} className="pl-6">
                        {
                          languageData
                            ?.map((languageItem, index) =>
                              <Col key={`${languageItem?.normalizedLanguageCode}${languageItem?.languageCode}`} xs={24}>
                                {renderLanguageItem(languageItem, index)}
                              </Col>
                            )
                        }
                      </Row>
                    </Collapse.Panel>
                  </Collapse>
                </Col>
              </Row>
              <Row hidden={contentChangesData && Object.values(contentChangesData)?.every(value => !value.length)}>
                <Col xs={24}>
                  <Collapse defaultActiveKey={['2']} ghost>
                    <Collapse.Panel
                      showArrow={false}
                      header={<FormSectionHeader title={<Fragment><WarningOutlined className="mt-1 text-warning"/> {t('Changes in exported content')}</Fragment>} />}
                      key="2"
                    >
                      <Row className="mb-4">
                        <Col xs={24}>
                          <Typography.Text className="fw-500">
                            {t('There are content updates to the exported flows, in primary language:')}
                          </Typography.Text>
                        </Col>
                      </Row>
                      <Row className="pl-6">
                        {
                          contentChangesData && Object.entries(contentChangesData)
                            ?.filter(([key, value]) => value?.length > 0)
                            ?.map(([key, value]) =>
                              <Col xs={24}>
                                {renderContentChangesItem(key, value)}
                              </Col>
                            )
                        }
                      </Row>
                    </Collapse.Panel>
                  </Collapse>
                </Col>
                <Col xs={24}>
                  <Row className="px-4 py-3">
                    <Typography.Text>
                      {t('Ignore content updates and proceed with')}
                    </Typography.Text>
                    <Typography.Text className="fw-500 ml-1 mr-1">
                      {t('Importing Translations')}
                    </Typography.Text>
                    <Typography.Text>
                      {t('or')}
                    </Typography.Text>
                    <Typography.Text className="text-link ml-1 mr-1 cursor-pointer" onClick={() => onClickExportTranslations()}>
                      {t('Export again')}
                    </Typography.Text>
                    <Typography.Text>
                      {t('the updated selected content in primary language and create new translations to Import.')}
                    </Typography.Text>
                  </Row>

                </Col>
              </Row>
            </Fragment>
          )
        }

      </Fragment>
    );
  };

  const renderStepFooter = (step) => {
    const footers = {
      [IMPORT_PROCESS_STEPS.FILE_UPLOAD]: (
        <Space>
          <Button
            size="large"
            onClick={onCancel}
          >
            {t('Cancel')}
          </Button>

          <Button
            size="large"
            type="primary"
            className="float-right"
            disabled={isEmptyArray(fileList)}
            onClick={onClickUpload}
            loading={isProcessingFiles}
          >
            {t('Upload')}
          </Button>
        </Space>
      ),
      [IMPORT_PROCESS_STEPS.PROCESS_CONTENT]: (
        <Row align="middle" justify="space-between" wrap={false}>
          <Col>
            <Typography.Text className="fs-sm">
              <WarningOutlined className="text-warning mr-1" />
              {t('Translated agent content will overwrite all existing agent content that matches.')}
            </Typography.Text>
          </Col>

          <Col>
            <Space>
              <Button
                size="large"
                onClick={onCancel}
              >
                {t('Cancel')}
              </Button>

              <Button
                size="large"
                type="primary"
                onClick={onImportClick}
                disabled={languageData?.every(item => !item.isEnabled) || isUpdatingBot || isUpdatingChapterSources || isEmptyObject(updatedEntities)}
                loading={isProcessingFiles || isUpdatingChapterSources}
              >
                {t('Import Translations')}
              </Button>
            </Space>
          </Col>
        </Row>
      )
    };
    return footers[step] || null;
  };

  const uploadPoProps = () => ({
    name: 'file',
    accept: Object.values(TRANSLATION_FILE_TYPES).filter(fileType => fileType.allowedImport).map(fileType => fileType.extension).join(', '),
    fileList: fileList,
    openFileDialogOnClick: true,
    onRemove: handleRemove,
    showUploadList: { showRemoveIcon: true, showPreviewIcon: false },
    className: 'ant-upload-w-100',
    beforeUpload: (file) => {
      setFileList([file]);
      return false;
    }
  });

  const renderUploadContent = () => {
    return (
      <Fragment>
        <Row>
          <Col xs={24} style={{ minHeight: '250px' }}>
            <Row align="middle" justify="center" className="mt-5 mb-5 h-100 w-100">
              <Col xs={24}>
                <Space direction="vertical">
                  <Button size="large">
                    {t('Select')}
                  </Button>
                  <Typography.Text>{t('or Drop File to this area')}</Typography.Text>
                </Space>
              </Col>
              <Col xs={24}>
                <Space direction="vertical">
                  <Typography.Text type="secondary">
                    {t(`Support for a single upload. Accepted file types: ${Object.values(TRANSLATION_FILE_TYPES).filter(fileType => fileType.allowedImport).map(fileType => fileType.extension).join(', ')}`)}
                  </Typography.Text>
                  <Typography.Text type="secondary">
                    {t('If multiple .po files then .zip is recommended')}
                  </Typography.Text>
                </Space>
              </Col>
            </Row>
          </Col>
        </Row>
      </Fragment>
    );
  };

  const renderUploadButton = () => {
    return (
      <Fragment>
        <Dragger {...uploadPoProps()}>{renderUploadContent()}</Dragger>
      </Fragment>);
  };

  return (
    <Fragment>
      <div className="px-6 pt-6 flex-grow-1">
        <Row>
          <Col xs={24}>
            {renderStepHeader(currentImportProcessStep)}
          </Col>
          <Col xs={24}>
            <Row>
              <Col xs={24}>
                {renderStepMainContent(currentImportProcessStep)}
              </Col>
            </Row>
          </Col>
        </Row>
      </div>

      <div className="ant-drawer-footer">
        <Row>
          <Col span={24}>
            <Space className="float-right" direction="horizontal">
              {renderStepFooter(currentImportProcessStep)}
            </Space>
          </Col>
        </Row>
      </div>

      <TranslationResultModal
        isModalOpen={isTranslationResultsModalOpen}
        onClose={() => {
          setIsTranslationResultsModalOpen(false);
          onUpdateComplete();
        }}
        importedEntities={updatedEntitiesNames}
        importedLanguages={languageData?.filter(item => item.isEnabled)?.map(item => Object.values(LANGUAGE_OPTIONS).find(lang => lang.key === item.normalizedLanguageCode).label)}
      />

      <TranslationExportModal
        isModalOpen={isDisplayExportModalOpen}
        exportedEntities={entitiesNamesToExport}
        onClose={() => {
          setIsDisplayExportModalOpen(false);
          setEntitiesNamesToExport([]);
          onUpdateComplete();
        }}
      />
    </Fragment>
  );
};

ImportTranslationsForm.propTypes = {
  onUpdateComplete: PropTypes.func,
  onClickNavigateToExport: PropTypes.func,
  onCancel: PropTypes.func
};

export default ImportTranslationsForm;
